import { useQuery } from "react-query";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import axiosClient from "../../../../../../lib/axios";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import { IDataDetalharTitulosReceber } from "../../../../../../types/api/financeiro/datalhar-titulos-receber";
import { Box, BoxProps, Button, Chip, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import LabelPrinter from "../../../../../../components/LabelPrinter";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import moment from "moment";
import DisplayContadores from "../../../../../../components/DisplayContadores";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import NotaFiscal from "./NotaFiscal";
import ValoresRecebidos from "./ValoresRecebidos";
import TitulosRelacionados from "./TitulosRelacionados";
import React, { useMemo, useState } from "react";
import DialogTableMov from "./DialogTableMov";
import CardDadosTitulo from "./CardDadosTitulo";
import CardDadosAdicionais from "./CardDadosAdicionais";
import tratativaDeDatas from "../../../../../../helpers/tratativaDeDatas";
import TitulosCompensados from "./TitulosCompensados";
import { ModalDetalheTituloPagarProvider } from "../../../MIContasPagar/ModalDetalheTituloContext";
import CardPedidosRelacionados from "./CardPedidosRelacionados";
import { ModalDetalhePedidoProvider } from "../../../../../Comercial/components/ModalDetalhePedidoContext";

export default function DialogDetalheTitulo({
    open,
    value,
    handleClose
}: DialogDetalheTituloProps) {

    const [openMov, setOpenMov] = useState(false)

    //querys e mutations
    const { data, isLoading } = useQuery({
        queryKey: ['financeiro/contas-receber/detalhar-titulo-receber', value],
        queryFn: () => axiosClient.get("/financeiro/contas-receber/detalhar-titulo-receber", {
            params: { id_titulo: value }
        }),
        cacheTime: 0
    });
    const detalhe: TDataQuery = data?.data || null
    //

    //funçoes manipuladoras
    const handleChangeMovDialog = (action: "open" | "close") => {
        if (action === "open") setOpenMov(true)
        if (action === "close") setOpenMov(false)
    }
    //

    //valores importantes
    const boxStyle: BoxProps = { component: Paper, p: 2, display: "flex", flexDirection: "column", gap: 3 }

    const VencimentoLabel = useMemo(() => {
        if (!detalhe?.data_vencimento || detalhe.saldo_titulo === 0) return <></>

        const diasVencimento = moment(detalhe.data_vencimento, "YYYY-MM-DD").diff(moment(), 'days')

        let textoItems = { color: "", label: "" }

        if (diasVencimento < 0) {
            textoItems.color = "error"
            textoItems.label = `Vencido há ${Math.abs(diasVencimento)} dias(s)`
        }
        if (diasVencimento === 0) {
            textoItems.color = "warning.main"
            textoItems.label = `Vence hoje`
        }
        if (diasVencimento === 1) {
            textoItems.color = "info.main"
            textoItems.label = `Vence amanhã`
        }
        if (diasVencimento > 1) {
            textoItems.color = "info.main"
            textoItems.label = `Vence em ${diasVencimento} dias(s)`
        }

        return <Typography variant="caption" color={textoItems.color}>{textoItems.label}</Typography>
    }, [detalhe])
    //


    return (
        <DialogFullScreen
            {...{ open, handleClose }}
            title="Detalhe do título a receber"
        >
            {isLoading && <LoadingApiQuery />}

            {
                (!isLoading && detalhe) &&
                <>
                    <Grid container spacing={2}>
                        <Grid container size={{ xs: 12, md: 4 }} spacing={2} alignSelf="flex-start">
                            <Grid size={{ xs: 12 }}>
                                <Box {...boxStyle}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <LabelPrinter
                                            titulo="Código"
                                            valor={value}
                                        />

                                        {
                                            (detalhe.data_recebimento) ?
                                                <Chip label={detalhe.saldo_titulo === 0 ? "Pago" : "Parcialmente Pago"} color="success" variant="outlined" /> :
                                                <Chip label="Em aberto" color="warning" variant="outlined" />
                                        }
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <LabelPrinter
                                            titulo="Saldo do título"
                                            valor={formatarValorMonetario(detalhe.saldo_titulo)}
                                        />

                                        <Stack direction="column">
                                            <LabelPrinter
                                                titulo="Vencimento"
                                                valor={moment(detalhe.data_vencimento).format("DD/MM/YYYY (ddd)").replace(/\((\w)/, (match, p1) => `(${p1.toUpperCase()}`)}
                                                alignTitle="flex-end"
                                            />
                                            {VencimentoLabel}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <LabelPrinter
                                            titulo="Emitido em"
                                            valor={moment(detalhe.data_emissao).format("DD/MM/YYYY")}
                                            pesoValorFonte={400}
                                            size="small"
                                        />

                                        <LabelPrinter
                                            titulo="Recebido em"
                                            valor={tratativaDeDatas(detalhe.data_recebimento)}
                                            pesoValorFonte={400}
                                            size="small"
                                        />
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <LabelPrinter
                                            titulo="Último pagamento"
                                            valor={tratativaDeDatas(detalhe.data_ultimo_pagamento)}
                                            pesoValorFonte={400}
                                            size="small"
                                        />
                                        {
                                            detalhe.recibo_vinculado &&
                                            <LabelPrinter
                                                titulo="Recibo de origem"
                                                valor={detalhe.recibo_vinculado}
                                                pesoValorFonte={600}
                                                alignTitle="flex-end"
                                                size="small"
                                                alignValue="right"
                                            />
                                        }
                                    </Stack>

                                    <Box>
                                        <DisplayContadores
                                            titulo="Valor original"
                                            valor={formatarValorMonetario(detalhe.valor_receber)}
                                        />

                                        <DisplayContadores
                                            titulo="Saldo principal"
                                            valor={formatarValorMonetario(detalhe.saldo_principal)}
                                        />

                                        <DisplayContadores
                                            titulo="Juros"
                                            valor={formatarValorMonetario(detalhe.valor_juros + (detalhe.valor_juros_acumulados || 0) + detalhe.valor_juros_capitalizado)}
                                        />

                                        <DisplayContadores
                                            titulo="Multa"
                                            valor={formatarValorMonetario(detalhe.valor_multa)}
                                        />

                                        <DisplayContadores
                                            titulo="Correção monetária"
                                            valor={formatarValorMonetario(detalhe.valor_correcao_monetaria + detalhe.valor_correcao_monetaria_acumulada)}
                                        />

                                        <DisplayContadores
                                            titulo="Descontos"
                                            valor={formatarValorMonetario(detalhe.valor_desconto || 0)}
                                        />
                                    </Box>

                                    <Stack direction="row" justifyContent="space-between">
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<RemoveRedEyeOutlinedIcon />}
                                            sx={{ fontSize: '0.75rem' }}
                                            onClick={() => handleChangeMovDialog("open")}
                                        >
                                            Ver movimentações
                                        </Button>
                                    </Stack>

                                    <Divider />

                                    <Typography fontWeight="bold" variant="h6">Valores recebidos</Typography>
                                    <ValoresRecebidos {...{ detalhe }} idTitulo={value} />
                                </Box>
                            </Grid>
                            {
                                detalhe.pedidos_relacionados && detalhe.pedidos_relacionados.length > 0 &&
                                <Grid size={{ xs: 12 }}>
                                    <ModalDetalhePedidoProvider>
                                        <CardPedidosRelacionados pedidos={detalhe.pedidos_relacionados} />
                                    </ModalDetalhePedidoProvider>
                                </Grid>
                            }

                            <Grid size={{ xs: 12 }}>
                                <Box {...boxStyle}>
                                    <Typography fontWeight="bold" variant="h6">Titulos relacionados</Typography>
                                    <TitulosRelacionados codigo={value} relacionados={detalhe.titulos_relacionados} />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <ModalDetalheTituloPagarProvider>
                                    <Box {...boxStyle}>
                                        <Typography fontWeight="bold" variant="h6">Títulos utilizados na compensação</Typography>
                                        <TitulosCompensados codigo={value} titulos={detalhe.tituloPagarCompensado} />
                                    </Box>
                                </ModalDetalheTituloPagarProvider>
                            </Grid>
                        </Grid>

                        <Grid container size={{ xs: 12, md: 8 }} spacing={2} alignSelf="flex-start">
                            <Grid size={{ xs: 12 }}>
                                <CardDadosTitulo
                                    {...{ boxStyle, detalhe }}
                                    codigo={value}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <CardDadosAdicionais
                                    {...{ boxStyle, detalhe }}
                                    codigo={value}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Box {...boxStyle}>
                                    <Typography variant="h6">Informações fiscais</Typography>
                                    <NotaFiscal
                                        titulo={value}
                                        publico={detalhe.publico}
                                        valores={detalhe.fiscal_movimento_saida}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    {
                        openMov &&
                        <DialogTableMov
                            open={openMov}
                            handleClose={() => handleChangeMovDialog("close")}
                            movimentacoes={detalhe?.movimentacoes}
                        />
                    }
                </>
            }

        </DialogFullScreen>
    )
}

export const DadosTitulosItems = ({
    titulo,
    valor
}: DadosTitulosItemsProps) => {
    return (
        <Stack>
            <Typography fontWeight="bold" variant="subtitle2">{titulo}</Typography>
            <Typography variant="subtitle2">{valor}</Typography>
        </Stack>
    )
}

interface DialogDetalheTituloProps {
    open: boolean,
    value: string,
    handleClose: () => void
}

interface DadosTitulosItemsProps {
    titulo: string,
    valor: string
}

type TDataQuery = IDataDetalharTitulosReceber | null